import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost } from "../utils/apiAuth";
import config from '../config';
import { ContentTypeConstants } from '../constants/contentCreationConstants';

const masterColumnStyles = {
  sno: { width: "50px", minWidth: "50px", textAlign: "center", fontWeight: 500, padding: "6px 2px" },
  subject: { width: "200px", minWidth: "200px", fontWeight: 500, padding: "6px 8px" },
  countWide: { width: "120px", minWidth: "120px", textAlign: "center", fontWeight: 500, color: "#1976d2", padding: "6px 2px" },
  count: { width: "100px", minWidth: "100px", textAlign: "center", fontWeight: 500, color: "#1976d2", padding: "6px 2px" },
};

function MasterPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [masterData, setMasterData] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch subjects first
      const subjectsResponse = await apiGet(`${config.API_BASE_URL}/api/get-subjects/`);
      const subjectsList = subjectsResponse.data.subjects || [];
      setSubjects(subjectsList);

      if (subjectsList.length === 0) {
        setMasterData([]);
        setLoading(false);
        return;
      }

      // Fetch content for all subjects
      const subjectIds = subjectsList.map(s => s.subject_id);
      const contentResponse = await apiPost(
        `${config.API_BASE_URL}/api/get-subject-content/`,
        { subject_ids: subjectIds }
      );

      const contentData = contentResponse.data?.content || {};
      
      // Process data to create master summary
      const masterSummary = subjectsList.map((subject, index) => {
        const subjectContent = contentData[subject.subject_name] || [];
        
        // Calculate totals
        let totalTopics = subjectContent.length;
        let totalSubTopics = 0;
        let totalVideos = 0;
        let totalNotes = 0;
        let totalMcq = 0;
        let totalCoding = 0;

        subjectContent.forEach((topic) => {
          if (topic.sub_topics) {
            totalSubTopics += topic.sub_topics.length;
            topic.sub_topics.forEach((subTopic) => {
              totalVideos += subTopic[ContentTypeConstants.VIDEO.key] || 0;
              totalNotes += subTopic[ContentTypeConstants.NOTES.key] || 0;
              totalMcq += subTopic[ContentTypeConstants.MCQ.key] || 0;
              totalCoding += subTopic[ContentTypeConstants.CODING.key] || 0;
            });
          }
        });

        return {
          sno: index + 1,
          subject_name: subject.subject_name,
          topics: totalTopics,
          sub_topics: totalSubTopics,
          videos: totalVideos,
          notes: totalNotes,
          mcq: totalMcq,
          coding: totalCoding,
        };
      });

      setMasterData(masterSummary);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch master data");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMasterData();
  }, []);

  // Get user role
  const userType = sessionStorage.getItem('Type') || '';
  const isCC = userType.includes('CC') && !userType.includes('AD');
  const shouldHideCountColumns = isCC;

  return (
    <div>
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          ) : masterData.length > 0 ? (
            <div className="table-responsive">
              <table
                className="table table-hover table-striped table-sm align-middle"
                style={{ minWidth: "800px" }}
              >
                <thead>
                  <tr style={{ background: "#f8f9fa" }}>
                    <th style={masterColumnStyles.sno}>S.no</th>
                    <th style={masterColumnStyles.subject}>Subject</th>
                    <th style={masterColumnStyles.count}>Topic</th>
                    <th style={masterColumnStyles.countWide}>Sub Topic</th>
                    {!shouldHideCountColumns && (
                      <>
                        <th style={masterColumnStyles.count}>Videos</th>
                        <th style={masterColumnStyles.count}>Notes</th>
                        <th style={masterColumnStyles.count}>MCQs</th>
                        <th style={masterColumnStyles.count}>Coding</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {masterData.map((item) => (
                    <tr key={item.sno}>
                      <td style={masterColumnStyles.sno}>{item.sno}</td>
                      <td style={masterColumnStyles.subject}>
                        {item.subject_name}
                      </td>
                      <td style={masterColumnStyles.count}>
                        {item.topics}
                      </td>
                      <td style={masterColumnStyles.countWide}>
                        {item.sub_topics}
                      </td>
                      {!shouldHideCountColumns && (
                        <>
                          <td style={masterColumnStyles.count}>
                            {item[ContentTypeConstants.VIDEO.key]}
                          </td>
                          <td style={masterColumnStyles.count}>{item.ContentTypeConstants.NOTES.key}</td>
                          <td style={masterColumnStyles.count}>{item.ContentTypeConstants.MCQ.key}</td>
                          <td style={masterColumnStyles.count}>
                            {item[ContentTypeConstants.CODING.key]}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-5 text-muted">
              <p>No master data available</p>
            </div>
          )}
    </div>
  );
}

export default MasterPage;

